if(!customElements.get('tab-switcher')) {
  class TabSwitcher extends HTMLElement {
    constructor() {
      super();
      this.bindOnButtonClick = this.onButtonClick.bind(this);
      this.bindOnMouseDown = this.onMouseDown.bind(this);
      this.bindOnMouseMove = this.onMouseMove.bind(this);
      this.bindOnMouseUp = this.onMouseUp.bind(this);
      this.isDragging = false;
      this.startX = 0;
      this.initScrollLeft = 0;
    }

    connectedCallback() {
      this.buttons = this.querySelectorAll('.tab-switcher__button');
      this.buttonsWrapper = this.querySelector('.tab-switcher__buttons');
      this.items = this.querySelectorAll('.tab-switcher__item');
      this.list = this.querySelector('.tab-switcher__list');
      this.hasHeightTransition = this.classList.contains('tab-switcher--height-transition');

      this.buttons.forEach((button) => {
        button.addEventListener('click', this.bindOnButtonClick);
      })
      if (this.classList.contains('tab-switcher--swipe-desktop') || this.classList.contains('tab-switcher--swipe-mobile')) {
        this.checkOverflow();

        this.resizeObserver = new ResizeObserver(() => {
          this.checkOverflow();
        });
        this.resizeObserver.observe(this.buttonsWrapper);
      }

    }

    updateHeight(newActiveItem) {
      if (!this.hasHeightTransition || !this.list || !newActiveItem) return;

      const currentHeight = this.list.offsetHeight;
      this.list.style.height = `${currentHeight}px`;

      requestAnimationFrame(() => {
        const newHeight = newActiveItem.offsetHeight;
        this.list.style.height = `${newHeight}px`;

        const resetHeight = () => {
          this.list.style.height = '';
          this.list.removeEventListener('transitionend', resetHeight);
        };
        this.list.addEventListener('transitionend', resetHeight);
      });
    }

    disconnectedCallback() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      this.buttons.forEach((button) => {
        button.removeEventListener('click', this.bindOnButtonClick);
      })
      if(this.buttonsWrapper) this.buttonsWrapper.removeEventListener('mousedown', this.bindOnMouseDown);
    }

    checkOverflow() {
      if (!this.buttonsWrapper || this.buttons.length === 0) return;
      if (window.innerWidth < 750 && !this.classList.contains('tab-switcher--swipe-mobile')) {
        this.buttonsWrapper.classList.remove('overflowed');
        return;
      }
      if (window.innerWidth >= 750 && !this.classList.contains('tab-switcher--swipe-desktop')) {
        this.buttonsWrapper.classList.remove('overflowed');
        return;
      }

      const containerWidth = this.buttonsWrapper.offsetWidth;
      const computedStyle = window.getComputedStyle(this.buttonsWrapper);
      const gap = parseFloat(computePixelValue(computedStyle.columnGap)) || parseFloat(computePixelValue(computedStyle.gap)) || 0;
      const paddingLeft = parseFloat(computePixelValue(computedStyle.paddingLeft)) || parseFloat(computePixelValue(computedStyle.paddingInline)) || parseFloat(computePixelValue(computedStyle.padding)) || 0;
      const paddingRight = parseFloat(computePixelValue(computedStyle.paddingRight)) || parseFloat(computePixelValue(computedStyle.paddingInline)) || parseFloat(computePixelValue(computedStyle.padding)) || 0;

      let totalButtonsWidth = 0;
      this.buttons.forEach((button) => {
        totalButtonsWidth += button.offsetWidth;
      });

      const totalGapsWidth = gap * (this.buttons.length - 1);
      const totalContentWidth = totalButtonsWidth + totalGapsWidth + paddingLeft + paddingRight;

      const isOverflowed = totalContentWidth > containerWidth;
      const wasOverflowed = this.buttonsWrapper.classList.contains('overflowed');

      if (isOverflowed && !wasOverflowed) {
        this.buttonsWrapper.classList.add('overflowed');
        this.buttonsWrapper.addEventListener('mousedown', this.bindOnMouseDown);
      } else if (!isOverflowed && wasOverflowed) {
        this.buttonsWrapper.classList.remove('overflowed');
        this.buttonsWrapper.removeEventListener('mousedown', this.bindOnMouseDown);
      }

      if (isOverflowed && this.classList.contains('tab-switcher--peek')) {
        this.applyPeekScroll(gap, paddingLeft, containerWidth);
      }
    }

    applyPeekScroll(gap, paddingLeft, containerWidth) {
      const MIN_PEEK = 12;
      let cumulativeRight = paddingLeft;

      for (const button of this.buttons) {
        const buttonLeft = cumulativeRight;
        const buttonRight = buttonLeft + button.offsetWidth;

        if (buttonRight > containerWidth) {
          const visiblePx = containerWidth - buttonLeft;
          if (visiblePx < MIN_PEEK) {
            const needed = buttonLeft + MIN_PEEK - containerWidth;
            this.buttonsWrapper.style.scrollBehavior = 'auto';
            this.buttonsWrapper.scrollLeft = needed;
            this.buttonsWrapper.style.scrollBehavior = '';
          }
          break;
        }

        cumulativeRight = buttonRight + gap;
      }
    }

    scrollTabIntoView(button) {
      if (!this.buttonsWrapper) return;
      const wrapperLeft = this.buttonsWrapper.scrollLeft;
      const wrapperRight = wrapperLeft + this.buttonsWrapper.offsetWidth;
      const btnLeft = button.offsetLeft;
      const btnRight = btnLeft + button.offsetWidth;

      if (btnLeft < wrapperLeft) {
        this.buttonsWrapper.scrollLeft = btnLeft;
      } else if (btnRight > wrapperRight) {
        this.buttonsWrapper.scrollLeft = btnRight - this.buttonsWrapper.offsetWidth;
      }
    }

    onMouseDown(event) {
      this.isDragging = true;
      this.startX = event.pageX - this.buttonsWrapper.offsetLeft;
      this.initScrollLeft = this.buttonsWrapper.scrollLeft;
      this.buttonsWrapper.classList.add('grabbing');

      this.buttonsWrapper.addEventListener('mousemove', this.bindOnMouseMove);
      this.buttonsWrapper.addEventListener('mouseup', this.bindOnMouseUp);
      this.buttonsWrapper.addEventListener('mouseleave', this.bindOnMouseUp);
    }

    onMouseMove(event) {
      if (!this.isDragging) return;
      event.preventDefault();
      this.buttonsWrapper.classList.add('moving');
      const x = event.pageX - this.buttonsWrapper.offsetLeft;
      const walk = x - this.startX;
      this.buttonsWrapper.scrollLeft = this.initScrollLeft - walk;
    }

    onMouseUp(event) {
      this.isDragging = false;
      this.buttonsWrapper.classList.remove('grabbing', 'moving');
      this.buttonsWrapper.removeEventListener('mousemove', this.bindOnMouseMove);
      this.buttonsWrapper.removeEventListener('mouseup', this.bindOnMouseUp);
      this.buttonsWrapper.removeEventListener('mouseleave', this.bindOnMouseLeave);
    }

    onButtonClick(event) {
      const trigger = event.target.closest('.tab-switcher__button');
      const newActiveItem = Array.from(this.items).find(item => item.id === trigger.dataset.tab);

      if (newActiveItem) {
        const tmpl = newActiveItem.querySelector('template.tab-switcher__template');
        if (tmpl) {
          newActiveItem.appendChild(tmpl.content.cloneNode(true));
          tmpl.remove();
        }

        this.updateHeight(newActiveItem);

        this.buttons.forEach(button => {
          button.classList.toggle('active', button === trigger)
          button.setAttribute('aria-selected', (button === trigger).toString())
        })
        this.items.forEach(item => {
          item.classList.toggle('active', item.id === trigger.dataset.tab)
        })

        this.scrollTabIntoView(trigger);
      }
    }
  }

  customElements.define('tab-switcher', TabSwitcher);
}